import {useRef, useState} from 'react';
import {stateUpdaterFn} from '@netx/core-next/utils/react';
// import {
// 	createSyntheticEvent,
// } from '@netx/core-next/utils/react';
import {useConstCallback, useDocumentKeyDown} from '../hooks';

// what do we need to know:
// amount of items (so we can loop through them)
// the active item index
// if the active item index is in view
// ability to bring the active item index into view
// we want to be able to disable arrow key/keyboard navigation
// we want to be able to disable the focus trap altogether
// and we want to know when the focus trap is active

const checkIndexLoop = (index: number, total: number) =>
	index > total - 1 ? 0 : index < 0 ? total - 1 : index;

const keepInBounds = (index: number, total: number) =>
	Math.max(Math.min(index, total - 1), 0);

export interface UseArrowFocusTrapParams {
	activeDescendantLabel?: string;
	disabled?: boolean;
	itemsCount: number;
	initialActiveItemIndex?: number;
	initialKeysNavigationDisabled?: boolean;
	pageSize?: number;
	isActiveItemIndexInView?: (index: number) => boolean;
	onChangeActiveItemIndex?: (index: number) => void;
	scrollToItemIndex?: (index: number) => void;
	onItemClick?: (
		event: React.MouseEvent<HTMLElement, MouseEvent>,
		index: number,
	) => void;
	onItemKeyClick?: (event: KeyboardEvent, index: number) => void;
	onItemKeyDown?: (event: KeyboardEvent, index: number) => void;
}

export const useArrowFocusTrap = ({
	activeDescendantLabel = 'arrow-focus-trap-item',
	disabled,
	itemsCount,
	initialActiveItemIndex = 0,
	initialKeysNavigationDisabled = false,
	pageSize = 25,
	isActiveItemIndexInView,
	onChangeActiveItemIndex,
	scrollToItemIndex,
	onItemClick,
	onItemKeyClick,
	onItemKeyDown,
}: UseArrowFocusTrapParams) => {
	const [activeItemIndex, _setActiveItemIndex] = useState<number>(
		initialActiveItemIndex,
	);
	const [isFocusTrapActive, setIsFocusTrapActive] = useState<boolean>(false);
	const [keysNavigationDisabled, setKeysNavigationDisabled] = useState<boolean>(
		initialKeysNavigationDisabled,
	);
	const [userDisabled, setUserDisabled] = useState<boolean>(false);
	const [clickKeysDisabled, setClickKeysDisabled] = useState<boolean>(false);

	const _disabled = disabled || userDisabled || itemsCount === 0;

	const justClickedItem = useRef<boolean>(false);

	const setActiveItemIndex = useConstCallback(
		(
			index: number | ((prevIndex: number) => number),
			skipScrollCheck = false,
		) => {
			if (_disabled) return;
			const newIndex = keepInBounds(
				stateUpdaterFn(index, activeItemIndex),
				itemsCount,
			);
			if (newIndex !== activeItemIndex) {
				_setActiveItemIndex(newIndex);
				if (!skipScrollCheck) scrollToItemIfNeeded(newIndex);
				onChangeActiveItemIndex?.(newIndex);
			}
		},
	);

	const scrollToItemIfNeeded = useConstCallback((index: number) => {
		!isActiveItemIndexInView?.(index) && scrollToItemIndex?.(index);
	});

	useDocumentKeyDown(
		(event) => {
			if (event.defaultPrevented || userDisabled) return;
			switch (event.key) {
				// next item
				case 'ArrowDown': {
					setActiveItemIndex((activeItemIndex) =>
						checkIndexLoop(activeItemIndex + 1, itemsCount),
					);
					return false;
				}
				// previous item
				case 'ArrowUp': {
					setActiveItemIndex((activeItemIndex) =>
						checkIndexLoop(activeItemIndex - 1, itemsCount),
					);
					return false;
				}
				// last item
				case 'End': {
					setActiveItemIndex(itemsCount - 1);
					return false;
				}
				// first item
				case 'Home': {
					setActiveItemIndex(0);
					return false;
				}
				// next page
				case 'PageDown': {
					setActiveItemIndex((activeItemIndex) =>
						keepInBounds(activeItemIndex + pageSize, itemsCount),
					);
					return false;
				}
				// previous page
				case 'PageUp': {
					setActiveItemIndex((activeItemIndex) =>
						keepInBounds(activeItemIndex - pageSize, itemsCount),
					);
					return false;
				}
				case 'Enter':
				case ' ': {
					if (clickKeysDisabled) return;
					if (onItemKeyClick) {
						onItemKeyClick?.(event, activeItemIndex);
						return false;
					}
				}
			}
			onItemKeyDown?.(event, activeItemIndex);
		},
		{
			disabled: _disabled || keysNavigationDisabled || !isFocusTrapActive,
		},
	);

	const blurTimeout = useRef<NodeJS.Timeout | null>(null);
	const hasFocus = useRef<boolean>(false);
	const handleContainerBlur = useConstCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			// blur will occur when moving to another element, even if that
			// element is a descendant of this
			// so, we need to account for that by setting a timeout, and if another
			// focus event happens in that time, we cancel the timeout and don't
			// deactivate the focus trap
			if (blurTimeout.current) clearTimeout(blurTimeout.current);
			// if (focusTimeout.current) clearTimeout(focusTimeout.current);
			blurTimeout.current = setTimeout(() => {
				setIsFocusTrapActive(false);
				hasFocus.current = false;
			}, 16);
		},
	);

	const handleContainerFocus = useConstCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			// see notes in `handleContainerBlur`
			if (blurTimeout.current) clearTimeout(blurTimeout.current);

			if (hasFocus.current) {
				setIsFocusTrapActive(true);
				return;
			}

			hasFocus.current = true;
			setIsFocusTrapActive(true);
		},
	);

	const handleItemClick = useConstCallback(
		(index: number) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
			setActiveItemIndex(index);
			if (!clickKeysDisabled && !event.defaultPrevented)
				onItemClick?.(event, index);
		},
	);

	const interactedWithItemTimeout = useRef<NodeJS.Timeout | null>(null);
	const handleItemInteractStart = useConstCallback(
		(index: number) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
			if (!_disabled || event.ctrlKey || event.button === 2) return;
			interactedWithItemTimeout.current &&
				clearTimeout(interactedWithItemTimeout.current);
			setActiveItemIndex((prevIndex) => {
				justClickedItem.current = true;
				interactedWithItemTimeout.current = setTimeout(() => {
					justClickedItem.current = false;
				}, 16);
				if (prevIndex === index) return prevIndex;
				return index;
			});
		},
	);

	// for `aria-activedescendant`
	const activeDescendantValue =
		!disabled && activeItemIndex > -1
			? `${activeDescendantLabel}-${activeItemIndex.toString()}`
			: undefined;

	return {
		activeDescendantValue,
		activeItemIndex:
			disabled || itemsCount === 0 || !isFocusTrapActive ? -1 : activeItemIndex,
		disabled: disabled || itemsCount === 0, //` _disabled,
		isFocusTrapActive,
		keysNavigationDisabled,

		setActiveItemIndex,
		setClickKeysDisabled,
		setKeysNavigationDisabled,
		setUserDisabled,

		handleContainerBlur,
		handleContainerFocus,
		handleItemClick,
		handleItemInteractStart,
	};
};
